import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "@/components/roster/shiftColours";
import { formatDateTimeIST } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { FileSpreadsheet, Send, FileOutput, FileText, Eye } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const BADGE = { approved: "bg-emerald-100 text-emerald-900", submitted: "bg-amber-100 text-amber-900", draft: "bg-slate-100 text-slate-800", rejected: "bg-rose-100 text-rose-900" };
const LABEL = { approved: "Approved", submitted: "Waiting for approval", draft: "Draft (not included)", rejected: "Sent back (not included)" };

export function FinalDownloads({ f }) {
  const base = `final_roster_${f.year}_${String(f.month).padStart(2, "0")}_v${f.version}`;
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" asChild data-testid={`final-view-${f.id}`}><Link to={`/final-rosters/${f.id}`}><Eye className="mr-1 h-4 w-4" /> View combined</Link></Button>
      <Button size="sm" variant="outline" onClick={() => downloadBlob(api, `/roster/final/${f.id}/export.xlsx`, `${base}.xlsx`)} data-testid={`final-xlsx-${f.id}`}><FileSpreadsheet className="mr-1 h-4 w-4" /> Excel</Button>
      <Button size="sm" variant="outline" onClick={() => downloadBlob(api, `/roster/final/${f.id}/export.pdf`, `${base}.pdf`)} data-testid={`final-pdf-${f.id}`}><FileText className="mr-1 h-4 w-4" /> PDF</Button>
      <Button size="sm" variant="outline" onClick={() => downloadBlob(api, `/roster/final/${f.id}/zoho.xlsx`, `${base}_zoho.xlsx`)} data-testid={`final-zoho-${f.id}`}><FileOutput className="mr-1 h-4 w-4" /> Zoho format</Button>
    </div>
  );
}

export function FinalRoster() {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const now = new Date();
  const [m, setM] = useState({ month: String(now.getMonth() + 1), year: String(now.getFullYear()) });
  const [busy, setBusy] = useState(false);
  const key = ["final-status", actingOrgId, m.year, m.month];
  const { data } = useQuery({ queryKey: key, staleTime: 0, refetchOnMount: "always",
    queryFn: async () => (await api.get(`/roster/final/status?year=${m.year}&month=${m.month}`)).data });
  const send = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/roster/final/send", { year: +m.year, month: +m.month });
      toast.success(`Final roster v${r.version} sent to Infiblu (${r.employee_count} employees)`);
      qc.invalidateQueries({ queryKey: key });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(String);
  return (
    <Card className="mt-5 p-6" data-testid="final-roster-card">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xl font-bold text-slate-900">Final roster report</p>
          <p className="text-sm text-slate-600">All approved rosters for the month are combined into one report and sent to the Infiblu super admin. Rosters still waiting for approval must be handled first.</p>
        </div>
        <div className="flex gap-2">
          <Select value={m.month} onValueChange={(v) => setM({ ...m, month: v })}>
            <SelectTrigger className="w-40" data-testid="final-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((n, i) => <SelectItem key={n} value={String(i + 1)}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={m.year} onValueChange={(v) => setM({ ...m, year: v })}>
            <SelectTrigger className="w-28" data-testid="final-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {data && <FinalBody data={data} busy={busy} onSend={send} />}
    </Card>
  );
}

function FinalBody({ data, busy, onSend }) {
  const c = data.counts;
  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2 text-sm font-semibold" data-testid="final-counts">
        <span className="rounded-full bg-emerald-100 px-3 py-1 text-emerald-900">{c.approved} approved</span>
        <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-900">{c.submitted} waiting</span>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-800">{c.draft + c.rejected} not submitted</span>
      </div>
      <div className="space-y-1">
        {data.plans.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`final-plan-${p.id}`}>
            <span className="font-semibold text-slate-900">{p.location_name}{p.department_name ? ` · ${p.department_name}` : ""}</span>
            <span className={`rounded-full px-2.5 py-0.5 font-semibold ${BADGE[p.status]}`}>{LABEL[p.status]}</span>
          </div>
        ))}
        {!data.plans.length && <p className="text-sm text-slate-500">No rosters for this month.</p>}
      </div>
      <Button disabled={!data.can_send || busy} onClick={onSend} data-testid="final-send-btn"><Send className="mr-1 h-4 w-4" /> {data.sent.length ? "Send updated final roster" : "Send final roster"}</Button>
      {!data.can_send && <p className="text-sm font-medium text-amber-800" data-testid="final-send-hint">{c.submitted ? `${c.submitted} roster(s) waiting for your approval. Approve or send them back first.` : "Approve at least one roster for this month first."}</p>}
      {data.sent.map((f) => (
        <div key={f.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-50 px-4 py-3" data-testid={`final-sent-${f.id}`}>
          <span className="text-sm text-slate-800"><b>Version {f.version}</b> · {f.employee_count} employees · {f.roster_count} roster(s) · sent by {f.sent_by} on {formatDateTimeIST(f.sent_at)}</span>
          <FinalDownloads f={f} />
        </div>
      ))}
    </div>
  );
}
